/**
 * DotGrid component from React Bits
 * https://reactbits.dev/backgrounds/dot-grid
 * Canvas-based interactive dot grid with GSAP animations
 *
 * Two local additions on top of the upstream component, both there to make it
 * testable and accessible rather than to change how it looks:
 *
 * - **`prefers-reduced-motion`.** The dots are displaced by pointer speed and
 *   by a click shockwave — motion triggered by interaction, which WCAG 2.3.3
 *   asks to be suppressible. Under the preference the displacement tweens are
 *   skipped and the grid stays still. The proximity COLOUR blend is kept: it is
 *   a colour change, not movement, and it is the only feedback that the grid is
 *   live. `onDotClick` still fires — it is functionality, not decoration, and
 *   dropping it would make the concept popovers unreachable for those users.
 * - **`data-dot-grid-ready`.** Set once the first grid has been measured and
 *   built. Dot positions are a function of the MEASURED wrapper size, so
 *   anything computing a dot centre — a story clicking one, chiefly — has to
 *   wait for that measure or it races `buildGrid` and clicks empty canvas.
 */

'use client';
import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { gsap } from 'gsap';
import { InertiaPlugin } from 'gsap/InertiaPlugin';

gsap.registerPlugin(InertiaPlugin);

const throttle = (func: (...args: unknown[]) => void, limit: number) => {
  let lastCall = 0;
  return function (this: unknown, ...args: unknown[]) {
    const now = performance.now();
    if (now - lastCall >= limit) {
      lastCall = now;
      func.apply(this, args);
    }
  };
};

interface Dot {
  cx: number;
  cy: number;
  xOffset: number;
  yOffset: number;
  _inertiaApplied: boolean;
}

export interface DotClickInfo {
  index: number;
  row: number;
  col: number;
  x: number;
  y: number;
}

export interface DotGridProps {
  dotSize?: number;
  gap?: number;
  baseColor?: string;
  activeColor?: string;
  proximity?: number;
  speedTrigger?: number;
  shockRadius?: number;
  shockStrength?: number;
  maxSpeed?: number;
  resistance?: number;
  returnDuration?: number;
  className?: string;
  style?: React.CSSProperties;
  onDotClick?: (dot: DotClickInfo, event: MouseEvent) => void;
  highlightedDots?: Set<number> | number[];
  highlightColor?: string;
}

function hexToRgb(hex: string) {
  // Handle rgb() format
  if (hex.startsWith('rgb')) {
    const match = hex.match(/\d+/g);
    if (match && match.length >= 3) {
      return {
        r: parseInt(match[0], 10),
        g: parseInt(match[1], 10),
        b: parseInt(match[2], 10),
      };
    }
  }
  // Handle hex format
  const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!m) return { r: 0, g: 0, b: 0 };
  return {
    r: parseInt(m[1], 16),
    g: parseInt(m[2], 16),
    b: parseInt(m[3], 16),
  };
}

const DotGrid: React.FC<DotGridProps> = ({
  dotSize = 16,
  gap = 32,
  baseColor = '#5227FF',
  activeColor = '#5227FF',
  proximity = 150,
  speedTrigger = 100,
  shockRadius = 250,
  shockStrength = 5,
  maxSpeed = 5000,
  resistance = 750,
  returnDuration = 1.5,
  className = '',
  style,
  onDotClick,
  highlightedDots,
  highlightColor,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isReady, setIsReady] = useState(false);
  const dotsRef = useRef<Dot[]>([]);
  const gridInfoRef = useRef({ cols: 0, rows: 0 });
  const pointerRef = useRef({
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    speed: 0,
    lastTime: 0,
    lastX: 0,
    lastY: 0,
  });

  const baseRgb = useMemo(() => hexToRgb(baseColor), [baseColor]);
  const activeRgb = useMemo(() => hexToRgb(activeColor), [activeColor]);
  const highlightRgb = useMemo(
    () => (highlightColor ? hexToRgb(highlightColor) : null),
    [highlightColor]
  );

  // Helper to check if dot index is highlighted
  const isHighlighted = useCallback(
    (index: number) => {
      if (!highlightedDots) return false;
      if (highlightedDots instanceof Set) return highlightedDots.has(index);
      return highlightedDots.includes(index);
    },
    [highlightedDots]
  );

  const circlePath = useMemo(() => {
    if (typeof window === 'undefined' || !window.Path2D) return null;

    const p = new Path2D();
    p.arc(0, 0, dotSize / 2, 0, Math.PI * 2);
    return p;
  }, [dotSize]);

  const buildGrid = useCallback(() => {
    const wrap = wrapperRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const { width, height } = wrap.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(dpr, dpr);

    const cols = Math.floor((width + gap) / (dotSize + gap));
    const rows = Math.floor((height + gap) / (dotSize + gap));
    const cell = dotSize + gap;

    const gridW = cell * cols - gap;
    const gridH = cell * rows - gap;

    const extraX = width - gridW;
    const extraY = height - gridH;

    const startX = extraX / 2 + dotSize / 2;
    const startY = extraY / 2 + dotSize / 2;

    const dots: Dot[] = [];
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const cx = startX + x * cell;
        const cy = startY + y * cell;
        dots.push({ cx, cy, xOffset: 0, yOffset: 0, _inertiaApplied: false });
      }
    }
    dotsRef.current = dots;
    gridInfoRef.current = { cols, rows };
    // Positions are derived from the measured size, so "ready" means measured
    // — not mounted. See the module docstring.
    setIsReady(dots.length > 0);
  }, [dotSize, gap]);

  useEffect(() => {
    if (!circlePath) return;

    let rafId: number;
    const proxSq = proximity * proximity;

    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const { x: px, y: py } = pointerRef.current;

      for (let i = 0; i < dotsRef.current.length; i++) {
        const dot = dotsRef.current[i];
        const ox = dot.cx + dot.xOffset;
        const oy = dot.cy + dot.yOffset;
        const dx = dot.cx - px;
        const dy = dot.cy - py;
        const dsq = dx * dx + dy * dy;

        // Determine base color - highlighted dots use highlightColor
        const dotHighlighted = isHighlighted(i);
        const dotBaseRgb =
          dotHighlighted && highlightRgb ? highlightRgb : baseRgb;
        const dotBaseColor =
          dotHighlighted && highlightColor ? highlightColor : baseColor;

        let style = dotBaseColor;
        if (dsq <= proxSq) {
          const dist = Math.sqrt(dsq);
          const t = 1 - dist / proximity;
          const r = Math.round(dotBaseRgb.r + (activeRgb.r - dotBaseRgb.r) * t);
          const g = Math.round(dotBaseRgb.g + (activeRgb.g - dotBaseRgb.g) * t);
          const b = Math.round(dotBaseRgb.b + (activeRgb.b - dotBaseRgb.b) * t);
          style = `rgb(${r},${g},${b})`;
        }

        ctx.save();
        ctx.translate(ox, oy);
        ctx.fillStyle = style;
        ctx.fill(circlePath);
        ctx.restore();
      }

      rafId = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(rafId);
  }, [
    proximity,
    baseColor,
    activeRgb,
    baseRgb,
    circlePath,
    isHighlighted,
    highlightRgb,
    highlightColor,
  ]);

  useEffect(() => {
    buildGrid();
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(buildGrid);
      wrapperRef.current && ro.observe(wrapperRef.current);
    } else {
      window.addEventListener('resize', buildGrid);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', buildGrid);
    };
  }, [buildGrid]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Read once per effect run rather than per event: the tweens below are the
    // only motion, and a preference change remounts nothing, so re-reading on
    // every throttled mousemove would cost a media query for no benefit.
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const onMove = (e: MouseEvent) => {
      const now = performance.now();
      const pr = pointerRef.current;
      const dt = pr.lastTime ? now - pr.lastTime : 16;
      const dx = e.clientX - pr.lastX;
      const dy = e.clientY - pr.lastY;
      let vx = (dx / dt) * 1000;
      let vy = (dy / dt) * 1000;
      let speed = Math.hypot(vx, vy);
      if (speed > maxSpeed) {
        const scale = maxSpeed / speed;
        vx *= scale;
        vy *= scale;
        speed = maxSpeed;
      }
      pr.lastTime = now;
      pr.lastX = e.clientX;
      pr.lastY = e.clientY;
      pr.vx = vx;
      pr.vy = vy;
      pr.speed = speed;

      const rect = canvas.getBoundingClientRect();
      pr.x = e.clientX - rect.left;
      pr.y = e.clientY - rect.top;

      // The pointer position above still updates, so the proximity colour
      // blend keeps working; only the displacement is suppressed.
      if (reduceMotion) return;

      for (const dot of dotsRef.current) {
        const dist = Math.hypot(dot.cx - pr.x, dot.cy - pr.y);
        if (speed > speedTrigger && dist < proximity && !dot._inertiaApplied) {
          dot._inertiaApplied = true;
          gsap.killTweensOf(dot);
          const pushX = dot.cx - pr.x + vx * 0.005;
          const pushY = dot.cy - pr.y + vy * 0.005;
          gsap.to(dot, {
            inertia: { xOffset: pushX, yOffset: pushY, resistance },
            onComplete: () => {
              gsap.to(dot, {
                xOffset: 0,
                yOffset: 0,
                duration: returnDuration,
                ease: 'elastic.out(1,0.75)',
              });
              dot._inertiaApplied = false;
            },
          });
        }
      }
    };

    const onClick = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const { cols } = gridInfoRef.current;

      // Find clicked dot for onDotClick callback
      const clickRadius = dotSize / 2 + 4;
      let clickedDotIndex = -1;
      let minDist = Infinity;

      for (let i = 0; i < dotsRef.current.length; i++) {
        const dot = dotsRef.current[i];
        const dist = Math.hypot(dot.cx - cx, dot.cy - cy);
        if (dist < clickRadius && dist < minDist) {
          minDist = dist;
          clickedDotIndex = i;
        }
      }

      // Call onDotClick if a dot was clicked
      if (clickedDotIndex !== -1 && onDotClick) {
        const dot = dotsRef.current[clickedDotIndex];
        const row = Math.floor(clickedDotIndex / cols);
        const col = clickedDotIndex % cols;
        onDotClick(
          {
            index: clickedDotIndex,
            row,
            col,
            x: dot.cx,
            y: dot.cy,
          },
          e
        );
      }

      // Shockwave effect. Suppressed under reduced motion — but note this sits
      // AFTER the `onDotClick` call above, deliberately: the callback is how a
      // concept popover opens, so gating it here would make the feature
      // unreachable rather than merely still.
      if (reduceMotion) return;

      for (const dot of dotsRef.current) {
        const dist = Math.hypot(dot.cx - cx, dot.cy - cy);
        if (dist < shockRadius && !dot._inertiaApplied) {
          dot._inertiaApplied = true;
          gsap.killTweensOf(dot);
          const falloff = Math.max(0, 1 - dist / shockRadius);
          const pushX = (dot.cx - cx) * shockStrength * falloff;
          const pushY = (dot.cy - cy) * shockStrength * falloff;
          gsap.to(dot, {
            inertia: { xOffset: pushX, yOffset: pushY, resistance },
            onComplete: () => {
              gsap.to(dot, {
                xOffset: 0,
                yOffset: 0,
                duration: returnDuration,
                ease: 'elastic.out(1,0.75)',
              });
              dot._inertiaApplied = false;
            },
          });
        }
      }
    };

    const throttledMove = throttle(onMove as (...args: unknown[]) => void, 50);
    window.addEventListener('mousemove', throttledMove as EventListener, {
      passive: true,
    });
    window.addEventListener('click', onClick);

    return () => {
      window.removeEventListener('mousemove', throttledMove as EventListener);
      window.removeEventListener('click', onClick);
      // Kill the per-dot inertia tweens. They target plain objects in a ref
      // rather than DOM nodes, so React unmounting the canvas does not stop
      // them: an elastic return runs for `returnDuration` seconds afterwards,
      // mutating objects nothing reads any more. Harmless in the app — the
      // draw loop is already cancelled — but it is real work on the GSAP
      // ticker, and in the story catalog it leaks across story boundaries,
      // where one story's shockwave is still running during the next one's
      // assertions. Found exactly that way.
      for (const dot of dotsRef.current) gsap.killTweensOf(dot);
    };
  }, [
    maxSpeed,
    speedTrigger,
    proximity,
    resistance,
    returnDuration,
    shockRadius,
    shockStrength,
    onDotClick,
    dotSize,
  ]);

  return (
    <div
      ref={wrapperRef}
      data-dot-grid-ready={isReady ? 'true' : undefined}
      className={`relative h-full w-full ${className}`}
      style={style}
    >
      <canvas
        ref={canvasRef}
        className='pointer-events-none absolute inset-0 h-full w-full'
      />
    </div>
  );
};

export default DotGrid;
export { DotGrid };
